import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Home, Dumbbell, Apple, Brain, User } from "lucide-react";

/**
 * The athlete primary navigation. Same five destinations rendered two ways:
 *  - Desktop (≥sm): horizontal pill row, sticks under the top navbar.
 *  - Mobile (<sm):  fixed bottom tab bar, thumb-reachable.
 *
 * Active tab is derived from `location.pathname`. Each tab's `match` array
 * lists the route prefixes that should highlight it (so /workouts AND
 * /programs both highlight the "Train" tab, etc.).
 */

const TABS = [
  { id: "home",      label: "Home",      to: "/dashboard", Icon: Home,     match: ["/dashboard", "/journey"] },
  { id: "train",     label: "Train",     to: "/programs",  Icon: Dumbbell, match: ["/programs", "/workouts", "/ai-generator"] },
  { id: "nutrition", label: "Nutrition", to: "/nutrition", Icon: Apple,    match: ["/nutrition"] },
  { id: "ai",        label: "AI",        to: "/ai-coach",  Icon: Brain,    match: ["/ai-coach", "/form-analysis"] },
  { id: "profile",   label: "Profile",   to: "/billing",   Icon: User,     match: ["/billing", "/coaching", "/settings"] },
];

const isActive = (path, matches) => matches.some(m => path === m || path.startsWith(m + "/"));

export const AthleteNav = () => {
  const { pathname } = useLocation();

  return (
    <>
      {/* Desktop pill row — slots under the top navbar */}
      <div className="hidden sm:block fixed top-[64px] left-0 right-0 z-40 bg-background/85 backdrop-blur-md border-b border-border"
           data-testid="athlete-nav-desktop">
        <div className="max-w-7xl mx-auto px-6 py-2 flex justify-center gap-2">
          {TABS.map(({ id, label, to, Icon, match }) => {
            const active = isActive(pathname, match);
            return (
              <Link
                key={id}
                to={to}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-all ${
                  active
                    ? "bg-primary text-white shadow-lg shadow-primary/30"
                    : "text-text-secondary hover:bg-surface hover:text-white"
                }`}
                data-testid={`athlete-tab-${id}-desktop`}>
                <Icon className="w-4 h-4" />
                {label}
              </Link>
            );
          })}
        </div>
      </div>

      {/* Mobile bottom bar — fixed, always-visible */}
      <div className="sm:hidden fixed bottom-0 left-0 right-0 z-40 bg-background/95 backdrop-blur-md border-t border-border pb-[env(safe-area-inset-bottom)]"
           data-testid="athlete-nav-mobile">
        <div className="flex justify-around px-2 pt-1.5 pb-1">
          {TABS.map(({ id, label, to, Icon, match }) => {
            const active = isActive(pathname, match);
            return (
              <Link
                key={id}
                to={to}
                className={`flex flex-col items-center gap-0.5 px-2 py-1.5 rounded-xl flex-1 transition-colors ${
                  active ? "bg-primary/10" : "active:bg-white/5"
                }`}
                data-testid={`athlete-tab-${id}-mobile`}>
                <Icon className={`w-5 h-5 ${active ? "text-primary" : "text-text-muted"}`}
                      style={active ? { filter: "drop-shadow(0 0 6px rgba(59,130,246,0.6))" } : undefined} />
                <span className={`text-[10px] font-bold ${active ? "text-white" : "text-text-muted"}`}>{label}</span>
              </Link>
            );
          })}
        </div>
      </div>
    </>
  );
};

export default AthleteNav;
